import { configRepo } from '@/repository/config'
import Facade from '@/Facade'
import { dialogRepo } from '@/repository/dialog'
import { gameRepo } from '@/repository/game'
import { navRepo } from '@/repository/nav'
import { preloadRepo } from '@/repository/preload'
import { translationRepo } from '@/repository/translation'
import { getPaytableConfig } from '@/ui-content/paytable'
import { getRulesConfig } from '@/ui-content/rules'
import brandLogoAtlasDefault from '@/assets/brandlogo0.json'
import { TournamentManager } from '@/managers/TournamentManager'
import brandLogoImageDefault from '@/assets/brandlogo0.png'
import { loadBrandLogoAssets, resolvePreloaderBrandingRule } from '@/assets/brandLogos'
import GameSignals from '@/signals/GameSignals'
import { buildAssetUrl, resolveAssetUrls } from '@/utils/AssetsUrlUtil'
import { ExternalUIBridgeManager } from '@modules/src/managers/ExternalUIBridgeManager'
import { getOperatorConfig } from '@modules/src/operatorConfig'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { getTournamentBadgeAnchorRect } from '@/ui/TournamentBadgeAnchor'
import { getGameEnginePath } from '@/utils/GameEnginePath'
import {
    ExternalUIAction,
    ExternalUIAutoplayStopSettings,
    IExternalUISnapshot,
    TTournamentBadgeConfig,
    TTournamentBadgeLayout,
} from '@modules/src/interfaces/IExternalUIBridge'

const DEFAULT_BRAND_LOGO_SPRITE_FILE = 'brandlogo0.json'
const STATIC_PRELOAD_LOGO_URL = buildAssetUrl('/assets/tnmt_assets/intro-logo48.png')

/** Trophy first, then standings, for as long as the badge is on screen. */
const TOURNAMENT_BADGE_ICON_ROTATION_MS = 10_000

/** Breathing room between the Sound button and the badge below it, in CSS pixels. */
const TOURNAMENT_BADGE_ANCHOR_GAP = 6

/** Never let the badge be pushed off the right edge, whatever the anchor measures. */
const TOURNAMENT_BADGE_MIN_EDGE_INSET = 4

// The published modules package types the badge with a single icon pair. The standings icon is
// carried in the same public JSON, so it is declared here rather than pinning a new package.
type TTournamentBadgeConfigWithRank = TTournamentBadgeConfig & {
    rankIcon?: string
    rankIconHover?: string
}

/** Prefer Title Case fallback when ML returns the same phrase in a different casing. */
const titleCaseUiLabel = (translated: string | null | undefined, fallback: string): string => {
    if (!translated) {
        return fallback
    }
    if (translated.toLowerCase() === fallback.toLowerCase()) {
        return fallback
    }
    return translated
}

const DEFAULT_BRAND_LOGO_ASSETS: { image: string; atlas: IExternalUISnapshot['preloadLogoAtlas'] } = {
    image: brandLogoImageDefault,
    atlas: brandLogoAtlasDefault,
}

// Extends the snapshot type with an optional second logo layer. Kept local
// (rather than editing IExternalUISnapshot's type here) because this game
// pins a prebuilt @dragonframework/modules package that hasn't been
// rebuilt/republished with these fields yet — see the note on
// resolvePreloaderBrandingRule in @/assets/brandLogos.
type ISnapshotWithSecondLogo = Partial<IExternalUISnapshot> & {
    promoFreeGamesCounter?: IExternalUISnapshot['promoFreeGamesCounter'] | null
    preloadLogo2Url?: string
    preloadLogo2Atlas?: IExternalUISnapshot['preloadLogoAtlas']
    // True when the layer-1 logo is still the untouched default (no site/network
    // override matched). Lets the preloader UI scope layout tweaks — e.g. the
    // logo/progress-bar order swap — to only the default brand.
    preloadLogoIsDefaultBrand?: boolean
    // Printed across the tournament badge in place of the countdown, currently the player's
    // standing while the badge shows the standings icon. Not on the prebuilt modules snapshot
    // type yet, so it rides here with the logo layers.
    tournamentBadgeText?: string
    // Caption above that value, "Rank" while the standings icon is up. Same reason as above for
    // riding here instead of on the prebuilt snapshot type.
    tournamentBadgeLabel?: string
    // Absolute URL to the operator's game-history / logs page, resolved from
    // OPERATOR_CONFIG.defaults.logsPath. Not present on the prebuilt modules
    // snapshot type yet, so it is declared here alongside the logo layers.
    // (The gameHistory uiText label rides in the uiText literal the same way
    // the existing freeRoundsLabel does — the modules uiText type is stale.)
    logsPath?: string
}

export class ExternalUiAdapter {
    private _unsubscribeAction: (() => void) | null = null
    private _unsubscribers: Array<() => void> = []
    private _tournamentBadgeConfigFromPublic: TTournamentBadgeConfigWithRank | null = null
    private _tournamentBadgeConfigLoadAttempted = false
    private _tournamentBadgeShowsRank = false
    private _tournamentBadgeRotationId: number | null = null
    private _tournamentBadgeResizeFrameId: number | null = null
    private _brandLogoAssets = DEFAULT_BRAND_LOGO_ASSETS
    private _brandLogoIsDefault = true
    private _brandLogo2Assets: { image: string; atlas: IExternalUISnapshot['preloadLogoAtlas'] } | null = null
    private _brandLogoLoadAttempted = false

    private normalizeVolume = (raw: unknown): number => {
        const value = Number(raw)
        if (!Number.isFinite(value)) return Number.NaN

        // Accept both 0..1 and 0..100 inputs; keep snapshot in 0..1 range.
        const normalized = value > 1 ? value / 100 : value
        return Math.max(0, Math.min(1, normalized))
    }

    private normalizeAutoplayCount = (raw: unknown): number => {
        const value = Number(raw)
        if (!Number.isFinite(value) || value <= 0) {
            return Number.MAX_SAFE_INTEGER
        }

        return Math.floor(value)
    }

    // Resolves the {serverIP} placeholder used in operator-config URLs to an
    // absolute backend base. Mirrors ServerRequests.resolveGameengineBaseUrl,
    // but always returns an absolute host (never '') because this feeds a
    // user-facing link opened in a new tab, not a Vite-proxied API call.
    private resolveServerBaseUrl(): string {
        const search = new URLSearchParams(window.location.search)
        const extParams = (Facade.urlParams as any)?.extParams || {}
        const explicitBase =
            search.get('api_url') || search.get('backend_url') || extParams.api_url || extParams.backend_url

        if (explicitBase) {
            return String(explicitBase).replace(/\/+$/, '')
        }

        return getGameEnginePath() || 'https://staging-games.dragongaming.com'
    }

    private resolveAccountId(): string {
        const params = new URLSearchParams(window.location.search)
        const initData: any = (Facade.model as any)?.initData || {}

        return String(params.get('account_id') || initData?.player?.account_id || initData?.account_id || '').trim()
    }

    // Builds the Game History link from OPERATOR_CONFIG.defaults.logsPath.
    // Returns '' when no logsPath is configured, which hides the button.
    private resolveLogsUrl(): string {
        const raw = String(getOperatorConfig().defaults?.logsPath || '').trim()
        if (!raw) {
            return ''
        }

        const resolved = raw.includes('{serverIP}')
            ? raw.replace(/\{serverIP\}/g, this.resolveServerBaseUrl())
            : raw

        const accountId = this.resolveAccountId()
        if (!accountId || /[?&]account_id=/.test(resolved)) {
            return resolved
        }

        const separator = resolved.includes('?') ? '&' : '?'
        return `${resolved}${separator}account_id=${encodeURIComponent(accountId)}`
    }

    start() {
        ExternalUIBridgeManager.mount(window)

        if (Facade.model) {
            ;(Facade.model as any).preferredAutoplayCount = Number.MAX_SAFE_INTEGER
        }

        void this.loadTournamentBadgeConfigFromPublic()
        void this.loadBrandLogoForSite()

        this.pushSnapshot()

        this._unsubscribeAction = ExternalUIBridgeManager.onAction(this.onAction)

        const pushSnapshot = () => this.pushSnapshot()
        const connectSignal = (signal: { connect: (fn: () => void) => { disconnect: () => void } } | null | undefined) => {
            if (!signal?.connect) {
                return
            }
            const connection = signal.connect(pushSnapshot)
            this._unsubscribers.push(() => connection.disconnect())
        }

        this._unsubscribers.push(
            gameRepo.subscribe(pushSnapshot),
            translationRepo.subscribe(pushSnapshot),
            configRepo.subscribe(pushSnapshot),
            dialogRepo.subscribe(pushSnapshot),
            navRepo.subscribe(pushSnapshot),
            preloadRepo.subscribe(pushSnapshot)
        )
        connectSignal(GameSignals.backendMessage)

        // The badge follows the HUD row, which the panel re-lays out on this same signal. Pushing on
        // the next frame lets that finish first, otherwise the row is measured at its old position.
        const resizeConnection = ModuleSignals.gameResized.connect(this.onGameResized)
        this._unsubscribers.push(() => resizeConnection.disconnect())
        // Refresh badge visibility when Re-Spins / FS end (promo may have been deferred).
        connectSignal((GameSignals as any).holdAndWinStop)
        connectSignal(GameSignals.bonusStopComplete)
        connectSignal(GameSignals.idle)
        connectSignal((GameSignals as any).promoFreeSpins)
        connectSignal((GameSignals as any).promoFreeSpinsAccepted)
        connectSignal((GameSignals as any).promoFreeSpinsDeclined)
        connectSignal((GameSignals as any).promoFreeSpinsSessionComplete)
    }

    stop() {
        this._unsubscribeAction?.()
        this._unsubscribeAction = null

        this._unsubscribers.forEach((unsubscribe) => unsubscribe())
        this._unsubscribers = []

        this.stopTournamentBadgeIconRotation()

        if (this._tournamentBadgeResizeFrameId !== null) {
            window.cancelAnimationFrame(this._tournamentBadgeResizeFrameId)
            this._tournamentBadgeResizeFrameId = null
        }

        ExternalUIBridgeManager.unmount()
    }

    private onGameResized = () => {
        if (this._tournamentBadgeResizeFrameId !== null) {
            return
        }

        this._tournamentBadgeResizeFrameId = window.requestAnimationFrame(() => {
            this._tournamentBadgeResizeFrameId = null
            this.pushSnapshot()
        })
    }

    private stopTournamentBadgeIconRotation(): void {
        if (this._tournamentBadgeRotationId === null) {
            return
        }

        window.clearInterval(this._tournamentBadgeRotationId)
        this._tournamentBadgeRotationId = null
    }

    /**
     * The badge alternates trophy and standings while it is visible. Hiding it puts the trophy back,
     * so every appearance starts from the same icon rather than from wherever the last one stopped.
     */
    private syncTournamentBadgeIconRotation(isBadgeVisible: boolean): void {
        if (!isBadgeVisible) {
            this.stopTournamentBadgeIconRotation()
            this._tournamentBadgeShowsRank = false
            return
        }

        if (this._tournamentBadgeRotationId !== null) {
            return
        }

        this._tournamentBadgeRotationId = window.setInterval(() => {
            this._tournamentBadgeShowsRank = !this._tournamentBadgeShowsRank
            this.pushSnapshot()
        }, TOURNAMENT_BADGE_ICON_ROTATION_MS)
    }

    /**
     * The player's standing, or NA when they have none yet — the same value and wording the
     * leaderboard tab shows, resolved the same way: the backend sends it on the player's own row
     * when it knows it, otherwise it is their position in the standings.
     */
    private resolveTournamentRankText(tournamentData: any): string {
        const playerScore = tournamentData?.player_score || []
        const explicitRank = playerScore?.[0]?.[2]

        if (explicitRank !== undefined && explicitRank !== null && String(explicitRank).trim() !== '') {
            const rank = String(explicitRank).trim().replace(/^#/, '')
            return rank.toUpperCase() === 'NA' ? '#NA' : `#${rank}`
        }

        const playerId = String(playerScore?.[0]?.[0] ?? '').trim()
        if (!playerId) {
            return '#NA'
        }

        const leaderboard = tournamentData?.all_players_score || []
        const position = leaderboard.findIndex((row: any) => String(row?.[0]) === playerId)

        return position >= 0 ? `#${position + 1}` : '#NA'
    }

    /**
     * Anchors a single layout under the Sound button, centred on it. Falls back to aligning the
     * right edges when the configured width is a CSS length this cannot do arithmetic on.
     */
    private anchorBadgeLayout(
        layout: TTournamentBadgeLayout | undefined,
        anchor: NonNullable<ReturnType<typeof getTournamentBadgeAnchorRect>>,
        fallbackWidth: unknown
    ): TTournamentBadgeLayout {
        const width = Number(layout?.width ?? fallbackWidth)
        const rightEdge = Number.isFinite(width)
            ? anchor.left + anchor.width / 2 + width / 2
            : anchor.left + anchor.width

        return {
            ...(layout || {}),
            top: Math.round(anchor.top + anchor.height + TOURNAMENT_BADGE_ANCHOR_GAP),
            right: Math.max(TOURNAMENT_BADGE_MIN_EDGE_INSET, Math.round(window.innerWidth - rightEdge)),
            bottom: 'auto',
            left: 'auto',
        }
    }

    /**
     * The config as the external UI should see it right now: the icon for the current rotation step,
     * and offsets measured from the HUD row instead of the hand-tuned ones the JSON ships.
     */
    private resolveTournamentBadgeConfig(
        base: TTournamentBadgeConfigWithRank | undefined,
        showsRank: boolean
    ): TTournamentBadgeConfigWithRank | undefined {
        if (!base) {
            return undefined
        }

        const resolved: TTournamentBadgeConfigWithRank = {
            ...base,
            icon: showsRank ? base.rankIcon : base.icon,
            iconHover: showsRank ? base.rankIconHover || base.rankIcon : base.iconHover,
        }

        const anchor = getTournamentBadgeAnchorRect()
        if (!anchor) {
            return resolved
        }

        const fallbackWidth = base.default?.width
        resolved.default = this.anchorBadgeLayout(base.default, anchor, fallbackWidth)
        resolved.landscape = this.anchorBadgeLayout(base.landscape, anchor, fallbackWidth)
        resolved.portrait = this.anchorBadgeLayout(base.portrait, anchor, fallbackWidth)

        return resolved
    }

    private async loadTournamentBadgeConfigFromPublic() {
        if (this._tournamentBadgeConfigLoadAttempted) {
            return
        }

        this._tournamentBadgeConfigLoadAttempted = true

        try {
            const response = await fetch(buildAssetUrl('/assets/json/TournamentBadgeConfig.json'), {
                cache: 'no-store',
            })

            if (!response.ok) {
                console.warn('[ExternalUiAdapter] TournamentBadgeConfig.json not loaded', {
                    status: response.status,
                })
                return
            }

            // The JSON ships its own `/assets/...` icon paths, which have to follow the deployment.
            const config = resolveAssetUrls((await response.json()) as TTournamentBadgeConfig)
            this._tournamentBadgeConfigFromPublic = config

            console.log('[ExternalUiAdapter] Tournament badge config loaded from public JSON')

            this.pushSnapshot()
        } catch (error) {
            console.warn('[ExternalUiAdapter] Failed to load TournamentBadgeConfig.json', error)
        }
    }

    private async loadBrandLogoForSite() {
        if (this._brandLogoLoadAttempted) {
            return
        }

        this._brandLogoLoadAttempted = true

        const siteCode = Facade.urlParams?.siteCode || Facade.urlParams?.site_code
        const networkCode = Facade.urlParams?.networkCode || Facade.urlParams?.network_code

        try {
            const rule = resolvePreloaderBrandingRule(siteCode, networkCode, 1)

            if (rule?.spriteFile && rule.spriteFile !== DEFAULT_BRAND_LOGO_SPRITE_FILE) {
                const assets = await loadBrandLogoAssets(rule.spriteFile, rule.spriteName)
                if (assets) {
                    this._brandLogoAssets = assets as typeof DEFAULT_BRAND_LOGO_ASSETS
                    this._brandLogoIsDefault = false
                }
            }
        } catch (error) {
            console.warn('[ExternalUiAdapter] Failed to load brand-specific preloader logo, using default', error)
        }

        // Optional second, independently-positioned logo layer (e.g. a
        // network-specific logo shown alongside the default one). Most
        // brands have no layer-2 rule, so this resolves to null and the
        // second logo simply never appears — no impact on other brands.
        try {
            const rule2 = resolvePreloaderBrandingRule(siteCode, networkCode, 2)

            if (rule2?.spriteFile) {
                const assets2 = await loadBrandLogoAssets(rule2.spriteFile, rule2.spriteName)
                if (assets2) {
                    this._brandLogo2Assets = assets2 as typeof DEFAULT_BRAND_LOGO_ASSETS
                }
            }
        } catch (error) {
            console.warn('[ExternalUiAdapter] Failed to load second preloader logo layer, skipping', error)
        }

        this.pushSnapshot()
    }

    private pushSnapshot() {
        const gameState = gameRepo.getState()
        const configState = configRepo.getState()
        const navState = navRepo.getState()
        const preloadState = preloadRepo.getState()
        const translate = translationRepo.getState().translate
        const spinData = Facade.model?.spinData || gameState.spinData
        const freeRound = spinData?.freeround
        const freeRoundsCurrent = Number(freeRound?.amount_left)
        const freeRoundsTotal = Number(freeRound?.total_amount_given)
        const freeRoundsActive =
            Boolean(freeRound?.is_active) && Number.isFinite(freeRoundsTotal) && freeRoundsTotal > 0
        // Tournament data from Model
        const tournamentData = Facade.model?.tournamentData || Facade.model?.initData?.tournament_data
        const tournamentBadgeConfigSource: TTournamentBadgeConfigWithRank | undefined =
            this._tournamentBadgeConfigFromPublic ||
            (Facade as any).model?.tournamentBadgeConfig ||
            (Facade as any).model?.initData?.tournament_badge_config ||
            tournamentData?.badge_config ||
            tournamentData?.trophy_config
        const showTournament = (Facade as any)._showTournament || false
        const showTournamentBadge = (Facade as any)._showTournamentBadge || false

        this.syncTournamentBadgeIconRotation(showTournamentBadge && !showTournament)
        const badgeShowsRank = this._tournamentBadgeShowsRank && !!tournamentBadgeConfigSource?.rankIcon
        const tournamentBadgeConfig = this.resolveTournamentBadgeConfig(tournamentBadgeConfigSource, badgeShowsRank)
        // The countdown belongs to the trophy; the standings icon carries the standing itself,
        // captioned so the bare "#NA" reads as a position.
        const tournamentBadgeText = badgeShowsRank ? this.resolveTournamentRankText(tournamentData) : undefined
        const tournamentBadgeLabel = badgeShowsRank ? titleCaseUiLabel(translate('Rank'), 'Rank') : undefined

        if (tournamentData) {
            console.log('[ExternalUiAdapter] Tournament snapshot:', {
                hasTournamentData: !!tournamentData,
                tournamentId: tournamentData?.tournament_id,
                showTournament,
            })
        }

        const facadeVolume = this.normalizeVolume(Facade.defaultConfig?.volume)
        const stateVolume = this.normalizeVolume((gameState as unknown as { volume?: number }).volume)
        const resolvedVolume = Number.isFinite(facadeVolume)
            ? facadeVolume
            : Number.isFinite(stateVolume)
              ? stateVolume
              : gameState.sound || gameState.music
                ? 1
                : 0

        const snapshot: ISnapshotWithSecondLogo = {
            preloadProgress: preloadState.isActive ? Number(preloadState.progress || 0) : 100,
            preloadActive: !!preloadState.isActive,
            preloadLogoUrl: STATIC_PRELOAD_LOGO_URL,
            preloadLogoAtlas: undefined,
            preloadLogoIsDefaultBrand: this._brandLogoIsDefault,
            preloadLogo2Url: this._brandLogo2Assets?.image,
            preloadLogo2Atlas: this._brandLogo2Assets?.atlas,
            infoPages: {
                paytable: getPaytableConfig(translate, Facade.model?.initData?.game?.rtp),
                rules: getRulesConfig(translate, Facade.model?.initData?.game?.rtp),
            },
            uiText: {
                // Sidebar uses uppercase keys; settings header is normalized to "Settings" in slot-ui.
                settings: translate('SETTINGS') || 'SETTINGS',
                paytable: translate('PAYTABLE') || 'PAYTABLE',
                rules: translate('RULES') || 'RULES',
                volume: translate('Volume') || 'Volume',
                freeRoundsLabel: translate('FREE SPINS') || 'Free Spins',
                options: translate('OPTIONS') || 'OPTIONS',
                soundEffects: translate('Sound Effects') || 'Sound Effects',
                ambienceSound: translate('Ambience Sound') || 'Ambience Sound',
                turboSpin: translate('Quick Spin') || 'Quick Spin',
                useSpacebarToSpin: translate('Press space bar to spin/stop') || 'Press space bar to spin/stop',
                autoSpin: translate('AUTO SPIN') || 'AUTO SPIN',
                autoPlay: translate('Auto Play') || 'Auto Play',
                startAutoSpins: translate('START AUTO SPINS') || 'Start Auto Spins',
                // Candyland EN ML uses sentence-case values for these keys; keep Title Case UI labels.
                stopOnAnyWin: titleCaseUiLabel(translate('Stop on Any Win'), 'Stop on Any Win'),
                stopOnSingleWinLimit: titleCaseUiLabel(translate('Stop on Single Win Limit'), 'Stop on Single Win Limit'),
                stopOnSessionLossLimit: titleCaseUiLabel(
                    translate('Stop on Session Loss Limit'),
                    'Stop on Session Loss Limit'
                ),
                // Row label is "History"; button keeps "Game History" / ML value.
                history: titleCaseUiLabel(translate('History'), 'History'),
                gameHistory: translate('Game History') || 'GAME HISTORY',
            },
            logsPath: this.resolveLogsUrl(),
            freeRoundsCounter: freeRoundsActive
                ? {
                      current: Number.isFinite(freeRoundsCurrent) ? Math.max(0, freeRoundsCurrent) : 0,
                      total: Number.isFinite(freeRoundsTotal) ? Math.max(0, freeRoundsTotal) : 0,
                  }
                : undefined,
            promoFreeGamesCounter: null,
            balance: gameState.balance,
            currencySymbol: gameState.currency_symbol,
            currentBet: gameState.current_bet,
            state: gameState.state,
            stopSpin: !!gameState.stop_spin,
            isUIActive: configState.isUIActive,
            showUI: configState.showUI,
            menu: navState.active?.menu || null,
            language: gameState.language,
            volume: resolvedVolume,
            sound: !!gameState.sound,
            music: !!gameState.music,
            isMobile: !!configState.isMobile,
            turboSpin: !!gameState.turbo_spin,
            spacebarSpin: !!gameState.spacebar_spin,
            dialogs: this.getDialogsSnapshot(),
            tournamentData: tournamentData || undefined,
            tournamentBadgeConfig: tournamentBadgeConfig || undefined,
            tournamentBadgeText,
            tournamentBadgeLabel,
            showTournament: showTournament || false,
            showTournamentBadge: showTournamentBadge || false,
        }

        ExternalUIBridgeManager.pushSnapshot(snapshot)
    }

    private getDialogsSnapshot(): IExternalUISnapshot['dialogs'] {
        return dialogRepo.getState().items.map((dialog) => {
            const close = () => dialogRepo.getState().close(dialog.id)

            return {
                id: dialog.id,
                variant: dialog.variant,
                key: dialog.key,
                title: dialog.title,
                text: dialog.text,
                style: dialog.style,
                html: dialog.html,
                isCloseable: dialog.isCloseable,
                overlay: dialog.overlay,
                onClose: dialog.isCloseable ? close : undefined,
                buttons: dialog.buttons?.map((button) => ({
                    label: button.label,
                    onClick: () => {
                        if (button.callback) {
                            button.callback(close)
                            return
                        }

                        close()
                    },
                })),
            }
        })
    }

    private applyAutoplayStopSettings = (
        send: (event: string, data?: any) => void,
        settings: ExternalUIAutoplayStopSettings
    ) => {
        // Keep stop conditions in AutoplayManager even when the player closes Settings
        // and starts Auto Spins from the in-game button (which only emits startAutoplay).
        if (Facade.model) {
            ;(Facade.model as any).preferredAutoplayStopSettings = { ...settings }
        }

        send('set_autoplay_stop_any_win', { value: !!settings.stopOnAnyWin })
        send('set_stop_single_win', {
            value: settings.stopOnWinLimit ? settings.stopSingleWinValue : null,
        })
        send('set_autoplay_stop_cash_decrease_enabled', { value: !!settings.stopOnLossLimit })
        send('set_stop_balance_lower', {
            value: settings.stopOnLossLimit ? settings.stopLossValue : null,
        })
        send('set_autoplay_stop_cash_decrease', {
            value: settings.stopOnLossLimit ? settings.stopLossValue : null,
        })
    }

    private onAction = (action: ExternalUIAction) => {
        const send = gameRepo.getState().send

        switch (action.type) {
            case 'open_settings':
                navRepo.getState().openNav('settings')
                configRepo.getState().changeUIActive(true)
                break
            case 'close_settings':
                if (action.value) {
                    this.applyAutoplayStopSettings(send, action.value)
                }
                configRepo.getState().changeUIActive(false)
                break
            case 'spin':
                send('clicked_play')
                break
            case 'stop':
                send('clicked_stop')
                break
            case 'set_bet':
                send('set_bet', { value: action.value })
                break
            case 'set_volume':
                {
                    const volume = this.normalizeVolume(action.value)
                    const isAudioEnabled = volume > 0

                    // Keep local UI state coherent even if backend does not echo intermediate volume updates.
                    if (Facade.defaultConfig) {
                        Facade.defaultConfig.volume = volume
                        Facade.defaultConfig.sound = isAudioEnabled
                        Facade.defaultConfig.music = isAudioEnabled
                    }

                    send('set_volume', { value: volume })
                    send('set_sound', { value: isAudioEnabled })
                    send('set_music', { value: isAudioEnabled })

                    this.pushSnapshot()
                }
                break
            case 'set_sound':
                send('set_sound', { value: action.value })
                break
            case 'set_music':
                send('set_music', { value: action.value })
                break
            case 'set_turbo_spin':
                send('set_turbo_spin', { value: action.value })
                break
            case 'set_spacebar_spin':
                send('set_spacebar_spin', { value: action.value })
                break
            case 'set_autoplay_count':
                if (Facade.model) {
                    ;(Facade.model as any).preferredAutoplayCount = this.normalizeAutoplayCount(action.value)
                }
                break
            case 'start_autospin':
                if (Facade.model) {
                    ;(Facade.model as any).preferredAutoplayCount = this.normalizeAutoplayCount(
                        action.value.autoplayCount
                    )
                }
                this.applyAutoplayStopSettings(send, action.value)
                send('set_autoplay', { value: action.value.autoplayCount })
                configRepo.getState().changeUIActive(false)
                break
            case 'open_tournament':
                // Request fresh leaderboard data and open popup
                ;(GameSignals as any).openTournament?.emit()
                break
            case 'open_tournament_popup':
                TournamentManager.instance.openFromBadge()
                break
            case 'close_tournament':
                TournamentManager.instance.close()
                break
            case 'join_tournament':
                // Emit signal to join tournament
                ;(GameSignals as any).joinTournament?.emit(action.tournamentId)
                break
            default:
                break
        }
    }
}

export default ExternalUiAdapter
